import React from 'react';
import { Link } from 'react-router-dom';

const ProductGrid = ({ products, loading, error }) => {
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="ml-4 text-gray-600">Loading products...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
          <p className="text-red-600 font-medium">Error loading products</p>
          <p className="text-red-500 text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 text-lg">No products found</p>
      </div>
    );
  }

  // Sort products by updatedAt - recently updated categories appear first
  const sortedProducts = [...products].sort((a, b) => {
    const dateA = new Date(a.updatedAt || a.createdAt || 0);
    const dateB = new Date(b.updatedAt || b.createdAt || 0);
    
    // Debug logging - remove after testing
    if (products.indexOf(a) === 0) {
      console.log('First product dates:', {
        name: a.name,
        updatedAt: a.updatedAt,
        createdAt: a.createdAt,
        dateA: dateA,
      });
    }
    
    return dateB - dateA; // Descending order (most recently updated first)
  });

  return (
    <div className="grid mt-8 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
      {sortedProducts.map((product) => (
        <Link 
          to={`/product/${product._id}`} 
          key={product._id} 
          className="block hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
        >
          <div className="bg-white p-4 rounded-lg border border-gray-100 h-full flex flex-col">
            <div className="w-full h-64 mb-4 overflow-hidden rounded-lg bg-gray-100">
              {(product.images?.[0]?.url || product.image?.[0]?.url) ? (
                <img 
                  src={product.images?.[0]?.url || product.image?.[0]?.url} 
                  alt={product.images?.[0]?.altText || product.image?.[0]?.altText || product.name}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div 
                className="w-full h-full bg-gray-200 rounded-lg flex items-center justify-center"
                style={{ display: (product.images?.[0]?.url || product.image?.[0]?.url) ? 'none' : 'flex' }}
              >
                <div className="text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-gray-400 text-sm mt-2">No image</span>
                </div>
              </div>
            </div>
            
            <div className="flex-grow flex flex-col justify-between">
              <h3 className="text-sm font-medium text-gray-900 mb-2 line-clamp-2 hover:text-blue-600 transition-colors">
                {product.name}
              </h3>
              <div className="flex items-center justify-between">
                <p className="text-gray-900 font-semibold text-lg">
                  ₹{typeof product.price === 'number' ? product.price.toFixed(2) : product.price}
                </p>
                {product.originalPrice && product.originalPrice > product.price && (
                  <p className="text-gray-500 text-sm line-through">
                    ₹{typeof product.originalPrice === 'number' ? product.originalPrice.toFixed(2) : product.originalPrice}
                  </p>
                )}
              </div>
              
              {product.inStock === false && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Out of Stock
                  </span>
                </div>
              )}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};

export default ProductGrid;
